import { useSelector } from 'react-redux';
import compact from 'lodash/compact';

import { selectIsDataFeedRateLimitingFeatureFlagOn } from 'core/userContext/selectors';
import { Column } from 'ui/indexPages/tables/header/constants';
import { pxToRem } from 'utils/convert';

import { TABLE_COLUMN_ID } from '../types';
import { useTranslations } from './useTranslations';

export type DataFeedColumn = Column & {
  featureFlag?: string;
  isSearchable?: boolean;
};

export const useColumns = (): { columns: DataFeedColumn[]; gridLayout: string } => {
  const { t } = useTranslations();
  const DataFeedRateLimiting = useSelector(selectIsDataFeedRateLimitingFeatureFlagOn);
  const featureFlags: Record<string, boolean> = { DataFeedRateLimiting };

  return {
    columns: [
      {
        id: TABLE_COLUMN_ID.ID as string,
        isSearchable: true,
        isSortable: true,
        label: t('tableColumn.id'),
      },
      {
        id: TABLE_COLUMN_ID.FEED_NAME as string,
        isSearchable: true,
        isSortable: true,
        label: t('tableColumn.feedName'),
      },
      {
        id: TABLE_COLUMN_ID.ALIAS as string,
        isSearchable: true,
        isSortable: true,
        label: t('tableColumn.alias'),
      },
      {
        id: TABLE_COLUMN_ID.FORMAT as string,
        isSearchable: true,
        isSortable: true,
        label: t('tableColumn.format'),
      },
      {
        id: TABLE_COLUMN_ID.FEED_URL as string,
        isSearchable: true,
        isSortable: true,
        label: t('tableColumn.feedUrl'),
      },
      {
        id: TABLE_COLUMN_ID.CREATED as string,
        isSearchable: false,
        isSortable: true,
        label: t('tableColumn.created'),
      },
      {
        featureFlag: 'DataFeedRateLimiting',
        id: TABLE_COLUMN_ID.RATE_LIMIT as string,
        isSearchable: false,
        isSortable: true,
        label: t('tableColumn.rateLimit'),
      },
      {
        id: TABLE_COLUMN_ID.BUTTON as string,
        isSearchable: false,
        isSortable: false,
        label: '',
      },
    ].filter(
      // Filter out any columns behind a feature flag
      (col) => !col.featureFlag || featureFlags[col.featureFlag],
    ),
    gridLayout: compact([
      `minmax(${pxToRem(75)}, 0.5fr)`, // ID
      `minmax(${pxToRem(150)}, 1.5fr)`, // Feed Name
      `minmax(${pxToRem(150)}, 1.5fr)`, // Alias
      `minmax(${pxToRem(75)}, 0.5fr)`, // Format
      `minmax(${pxToRem(125)}, 1.25fr)`, // Feed URL
      `minmax(${pxToRem(100)}, 1fr)`, // Created
      DataFeedRateLimiting && `minmax(${pxToRem(100)}, 1fr)`, // Rate Limit (requests per minute)
      `minmax(${pxToRem(125)}, 0.75fr)`, // [button]
    ]).join(' '),
  };
};
